import React, { FC, useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  TextField,
  IconButton,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HealingIcon from '@mui/icons-material/Healing';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import EventNoteIcon from '@mui/icons-material/EventNote';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import DescriptionIcon from '@mui/icons-material/Description';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SearchIcon from '@mui/icons-material/Search';

import { useGetLandingQuery, useGetTrendsQuery, useGetResourceQuery, useNotesFeedInfiniteQuery, useGetEncountersQuery } from './healthWalletSlice';
import { isVisitUpcoming, isResourceUpcoming } from './RendererV1/classify';
import { V1_ACCENT, V1_ACCENT_TINT, V1_ACCENT_BORDER } from './RendererV1/theme';
import type { GroupedSummary } from './healthWalletConstants';
import { GroupedDetailDialog, GroupedRow } from './GroupedDetailDialog';
import { NotesSection } from './NotesSection';
import { UserDocumentsSection } from './UserDocumentsSection';
import { TabRenderer } from './RendererV1/TabRenderer';
import { TAB_SECTIONS } from './RendererV1/tabSections';
import { ConditionDetailDialog } from './RendererV1/ConditionDetailDialog';
import { MedicationDetailDialog } from './RendererV1/MedicationDetailDialog';
import { ProcedureDetailDialog } from './RendererV1/ProcedureDetailDialog';
import { ObservationDetailDialog } from './RendererV1/ObservationDetailDialog';

// ============================================================================
// HWRecordRendererV1 — the NEW-DESIGN workbench (2026-07-12, Leo): starts as a
// copy of HWRecordRenderer and will be REWORKED to the design team's MVP-polish
// spec for health-record rendering (the design maps onto the same slice hooks
// and master→detail structure). Same contract: the slice hooks ARE the data API
// + two optional AI-seam callbacks (onAskPolly / onActiveTabChange — the chat
// ENGINE is global in the shell; this file owns the Ask-Polly BAR placement).
// HWRecordRenderer stays the CURRENT/stable UI; the Settings "Advanced" toggle
// swaps between them for side-by-side comparison during the redesign.
// ============================================================================

// --- Categories ---
const CATEGORIES = [
  { key: 'trends', label: 'Trends', icon: <TrendingUpIcon fontSize="small" /> },
  { key: 'conditions', label: 'Conditions', icon: <HealingIcon fontSize="small" /> },
  { key: 'medications', label: 'Medications', icon: <LocalHospitalIcon fontSize="small" /> },
  { key: 'encounters', label: 'Encounters', icon: <EventNoteIcon fontSize="small" /> },
  { key: 'upcoming', label: 'Upcoming', icon: <EventAvailableIcon fontSize="small" /> },
  { key: 'procedures', label: 'Procedures', icon: <MedicalServicesIcon fontSize="small" /> },
  { key: 'observations', label: 'Observations', icon: <MonitorHeartIcon fontSize="small" /> },
  { key: 'allergies', label: 'Allergies', icon: <WarningAmberIcon fontSize="small" /> },
  { key: 'notes', label: 'Notes', icon: <DescriptionIcon fontSize="small" /> },
  { key: 'mydocuments', label: 'My Documents', icon: <FolderSharedIcon fontSize="small" /> },
];

// Records screen sizes to the viewport (small % gutter on larger screens) rather than a
// fixed MUI breakpoint cap — must match the shell's PAGE_WIDTH_SX so sections align.
const PAGE_WIDTH_SX = { maxWidth: { xs: '100%', sm: '94%' } };

// Structured category tabs that fetch a grouped list via get_patient_resource (the rest are custom panels).
const STRUCTURED = ['conditions', 'medications', 'encounters', 'procedures', 'observations', 'allergies'];

// ── Background tab-pill prefetch (Leo 2026-08-02) ────────────────────────────────────────────────────────
// The pills went label-only when we split records into lazy per-tab slices (no cheap way to get the REAL
// deduped counts without loading each slice) — but bare pills leave a patient unsure they even have records.
// Fix: AFTER the default tab has loaded, warm every getResource-backed tab's slice in the BACKGROUND, throttled,
// so (a) each pill shows its real deduped count and (b) clicking the tab is instant (cache hit). The tabs whose
// count/data already come free are excluded: encounters (getEncounters, fires on mount), trends (getTrends,
// fires on mount), notes + mydocuments (counts on `landing`). `encounters` is dropped here — its tab reads
// getEncounters, not getResource('encounters').
const PREFETCH_RESOURCE_TABS = ['conditions', 'medications', 'procedures', 'observations', 'allergies'];
const PREFETCH_CONCURRENCY = 2; // how many background slice fetches may be in flight at once

// One tab's getResource subscription — renders nothing; used purely to PREFETCH the slice + surface its group
// count. RTK dedupes with the active tab's self-fetching sections, so the active tab still costs a single fetch.
const ResourceCountProbe: FC<{
  tab: string;
  enabled: boolean;
  onCount: (tab: string, count: number) => void;
  onSettled: () => void;
}> = ({ tab, enabled, onCount, onSettled }) => {
  const { data, isSuccess, isError } = useGetResourceQuery(tab, { skip: !enabled });
  useEffect(() => {
    if (data) onCount(tab, data.named.length + (data.other ? 1 : 0)); // group count = what the tab's cards show
  }, [tab, data, onCount]);
  useEffect(() => {
    if (isSuccess || isError) onSettled(); // done → free a slot so the next queued tab can start
  }, [isSuccess, isError, onSettled]);
  return null;
};

// A FILTERABLE grouped-record list (shared by the Trends tab and every structured tab). A search box narrows
// by group name client-side; the full (filtered) list renders as plain cards and rides the native page scroll
// (no pagination/sort/virtualization — decided with Leo: the group rows are lightweight metadata, and native
// mobile momentum scroll handles the length; 645 cards is fine). Search state resets per tab via a `key`.
const GroupList: FC<{
  groups: GroupedSummary[];
  loading: boolean;
  emptyLabel: string;
  onSelect: (g: GroupedSummary) => void;
}> = ({ groups, loading, emptyLabel, onSelect }) => {
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const filtered = q ? groups.filter((g) => (g.name || 'other').toLowerCase().includes(q)) : groups;

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: V1_ACCENT }} /></Box>;
  }
  if (groups.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, bgcolor: 'white' }}>
        <Typography color="text.secondary">No {emptyLabel} recorded.</Typography>
      </Paper>
    );
  }
  return (
    <>
      {groups.length > 8 && (
        <TextField
          size="small" fullWidth placeholder={`Filter ${emptyLabel}…`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2, bgcolor: 'white', borderRadius: 2 }}
          InputProps={{ startAdornment: <SearchIcon sx={{ color: V1_ACCENT, mr: 1 }} fontSize="small" /> }}
        />
      )}
      {filtered.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 2 }}>No matches for “{search}”.</Typography>
      ) : (
        filtered.map((g, i) => <GroupedRow key={`${g.name}-${i}`} group={g} onClick={() => onSelect(g)} />)
      )}
    </>
  );
};

// The renderer's ONLY props — the AI seam (Leo 2026-07-12, siblings design): the CHAT ENGINE
// (Chatbot widget + conversation state) is GLOBAL, owned by the shell so history survives renderer
// swaps and the shell can place the widget anywhere (true global feel). The renderer owns the AI
// PRESENTATION (the Ask-Polly bar, wherever its design wants it) and talks UP: onAskPolly sends a
// query; onActiveTabChange keeps the shell's contextHint current (CEO ask — Polly knows which tab
// the user is on, for questions typed in the bar AND inside the chat widget).
export interface HWRecordRendererV1Props {
  onAskPolly?: (query: string) => void;
  onActiveTabChange?: (tab: string) => void;
}

export const HWRecordRendererV1: FC<HWRecordRendererV1Props> = ({ onAskPolly, onActiveTabChange }) => {
  const [activeCategory, setActiveCategory] = useState('conditions'); // default tab (Leo 2026-07-14; was notes)
  const [detailGroup, setDetailGroup] = useState<GroupedSummary | null>(null);
  // Redesigned-tab drill-down (RendererV1 dialog); legacy tabs keep GroupedDetailDialog.
  const [v1Group, setV1Group] = useState<GroupedSummary | null>(null);
  const topQueryRef = useRef('');
  const handleTopQuerySubmit = () => {
    const query = topQueryRef.current.trim();
    if (!query) return;
    onAskPolly?.(query);
    topQueryRef.current = '';
  };
  // Report the active tab up — the shell forwards it to Polly as contextHint.
  useEffect(() => { onActiveTabChange?.(activeCategory); }, [activeCategory, onActiveTabChange]);

  // The shell mounts this component only when authenticated with landing data present,
  // so the hooks run unskipped; RTK dedupes them against the shell's own reads.
  // Landing = ONE cheap call: patient header + per-category counts + notes page 1 (the default tab).
  const { data: landing } = useGetLandingQuery();
  // Trends: fires on mount but OFF the landing critical path (~2s: count + series). The Trends pill is
  // DISABLED once this returns no rows — nothing to chart — so patients with sparse records can't open an
  // empty tab (Leo 2026-07-26). Not part of the landing.
  const { data: trends, isLoading: isTrendsLoading } = useGetTrendsQuery();
  // Structured category tab (conditions/…/allergies) → tier-1 grouped list, lazy on tab open.
  const isStructuredTab = STRUCTURED.includes(activeCategory);
  const { data: resourceData, isFetching: isResourceFetching } = useGetResourceQuery(activeCategory, { skip: !isStructuredTab });
  // Encounters + procedures reads (RTK dedupes with the tab sections) purely to decide whether the
  // Upcoming pill appears — it shows when a scheduled/future visit OR a planned procedure exists.
  const { data: encounterVisits } = useGetEncountersQuery();
  const { data: proceduresResource } = useGetResourceQuery('procedures');
  const hasUpcoming =
    (encounterVisits ?? []).some(isVisitUpcoming) ||
    (proceduresResource?.named ?? []).some((g) => isResourceUpcoming(g, 'procedures'));
  // Notes feed: the landing supplies an INSTANT page-1 teaser (fast first paint at the server's env page size);
  // the paged body is an RTK infiniteQuery kept LAZY — skipped until the first "Show more"/scroll — so it doesn't
  // fetch on mount and defeat the teaser. It appends pages 2+ (same env size), so the displayed list is
  // teaser ++ feedPages; each pull adds one page instead of re-fetching a bigger page 1. RTK accumulates the
  // pages and resets them on a ['Patient'] invalidation (a sync).
  const [notesFeedStarted, setNotesFeedStarted] = useState(false);
  const { data: notesFeedData, isFetching: isNotesFetching, hasNextPage, fetchNextPage } =
    useNotesFeedInfiniteQuery(undefined, { skip: !notesFeedStarted });
  const feedNotes = [
    ...(landing?.notes?.notes ?? []),                          // page 1 (the teaser)
    ...(notesFeedData?.pages.flatMap((p) => p.notes) ?? []),   // pages 2+ (appended)
  ];
  const feedTotal = landing?.notes?.total ?? 0;
  const onNotesLoadMore = () => {
    if (!notesFeedStarted) { setNotesFeedStarted(true); return; } // first pull: start the feed (fetches page 2)
    if (hasNextPage) fetchNextPage();                             // subsequent: append the next page
  };

  // Trends → GroupedSummary rows (hasChart badge + resource chip); the series live inline on `trends`.
  const trendGroups: GroupedSummary[] = (trends ?? []).map((t) => ({
    name: t.name, count: t.count, firstDate: t.firstDate ?? '', lastDate: t.lastDate ?? '', hasChart: true, resource: t.resource,
  }));
  // No trend series → disable the Trends pill (below). Gate on the load finishing so a slow ~2s return
  // doesn't flash the pill disabled before the series arrive.
  const trendsUnavailable = !isTrendsLoading && trendGroups.length === 0;
  // Structured tab → named groups + the nameless "Other" bucket, in one filterable list.
  const structuredGroups: GroupedSummary[] =
    (resourceData ? [...resourceData.named, ...(resourceData.other ? [resourceData.other] : [])] : []) as GroupedSummary[];

  // ── Tab-pill counts via background prefetch (see PREFETCH_RESOURCE_TABS note) ──────────────────────────
  // Real deduped group counts, warmed after the default tab lands. `tabCounts` holds the getResource-backed
  // ones (filled by the probes below); the rest derive from already-loaded data. undefined = still loading
  // (the pill shows a tiny spinner); a number (incl. 0) = show it.
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({});
  const setCount = useCallback((tab: string, count: number) => {
    setTabCounts((p) => (p[tab] === count ? p : { ...p, [tab]: count }));
  }, []);
  // Throttle: only start warming the background tabs once the DEFAULT tab has loaded (so prefetch never
  // contends with / delays the tab the user is actually looking at). A sliding window of PREFETCH_CONCURRENCY
  // advances as each probe settles.
  const defaultTabReady = landing !== undefined && (!isStructuredTab || resourceData !== undefined);
  const [warmSlots, setWarmSlots] = useState(PREFETCH_CONCURRENCY);
  const advanceQueue = useCallback(() => setWarmSlots((n) => n + 1), []);

  const upcomingCount =
    (encounterVisits?.filter(isVisitUpcoming).length ?? 0) +
    ((proceduresResource?.named ?? []).filter((g) => isResourceUpcoming(g, 'procedures')).length);

  // The number a pill shows (undefined → still loading → spinner). getResource tabs come from the probes;
  // everything else is already in hand (landing / trends / encounters).
  const pillCount = (key: string): number | undefined => {
    switch (key) {
      case 'trends':      return isTrendsLoading ? undefined : trendGroups.length;
      case 'encounters':  return encounterVisits ? encounterVisits.length : undefined;
      case 'upcoming':    return upcomingCount;
      case 'notes':       return landing ? feedTotal : undefined;
      case 'mydocuments': return landing ? landing.counts.user_documents : undefined;
      default:            return tabCounts[key]; // conditions/medications/procedures/observations/allergies
    }
  };

  return (
    <>
      {/* Background prefetch probes (render nothing): warm the getResource tabs + surface their counts, once the
          default tab is ready, throttled to PREFETCH_CONCURRENCY. The active tab is always enabled (dedupes with
          its own sections). */}
      {PREFETCH_RESOURCE_TABS.map((tab, i) => (
        <ResourceCountProbe
          key={tab}
          tab={tab}
          enabled={tab === activeCategory || (defaultTabReady && i < warmSlots)}
          onCount={setCount}
          onSettled={advanceQueue}
        />
      ))}

      {/* Ask Polly bar — moved from the shell header (the AI entry point is a RENDERER design
          decision now, and doesn't have to sit at the very top — Leo). Tighter on mobile
          (Leo 2026-07-14: pull the bar up under the hero). */}
      <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: { xs: 0.5, sm: 2 } }}>
        <Paper
          elevation={0}
          sx={{
            display: 'flex', alignItems: 'center', px: 2, py: 0.5,
            borderRadius: 50, bgcolor: 'white',
            border: `1px solid ${V1_ACCENT_BORDER}`,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}
        >
          <AutoAwesomeIcon sx={{ color: V1_ACCENT, mr: 1 }} aria-hidden="true" />
          <TextField
            fullWidth variant="standard" placeholder="Ask Polly, your AI Health Assistant anything..."
            aria-label="Ask Polly, your AI Health Assistant"
            autoComplete="off"
            defaultValue=""
            onChange={(e) => { topQueryRef.current = e.target.value; }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleTopQuerySubmit(); }}
            InputProps={{ disableUnderline: true, sx: { py: 1 } }}
            sx={{ '& .MuiInputBase-input': { fontSize: '0.95rem' } }}
          />
          <IconButton onClick={handleTopQuerySubmit} aria-label="Send question to Polly" sx={{ color: V1_ACCENT }}>
            <SendIcon fontSize="small" />
          </IconButton>
        </Paper>
      </Container>

      {/* ("Your Health Wallet" heading removed 2026-07-13, Leo — the shell hero already says
          Health Wallet / Hi, {name}; a second title above the pills was repetition.) */}
      {/* Category Chips */}
      <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
        <Paper elevation={2} sx={{ borderRadius: 3, p: 2, bgcolor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
          <Box component="nav" aria-label="Health data categories" role="navigation" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {CATEGORIES.map((cat) => {
              // Upcoming pill appears ONLY when there's at least one scheduled/future visit.
              if (cat.key === 'upcoming' && !hasUpcoming) return null;
              const isActive = activeCategory === cat.key;
              // Trends pill is disabled when the series query returned nothing to chart (sparse records).
              const isDisabled = cat.key === 'trends' && trendsUnavailable;
              // Real deduped count from the background prefetch; undefined = still loading → tiny spinner.
              const count = pillCount(cat.key);
              return (
                <Chip
                  key={cat.key}
                  icon={cat.icon}
                  label={
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
                      {cat.label}
                      {count === undefined
                        ? <CircularProgress size={12} thickness={6} sx={{ color: 'inherit', opacity: 0.5 }} />
                        : <Box component="span" sx={{ fontWeight: 700 }}>{count}</Box>}
                    </Box>
                  }
                  disabled={isDisabled}
                  onClick={() => setActiveCategory(cat.key)}
                  aria-current={isActive ? 'true' : undefined}
                  variant={isActive ? 'filled' : 'outlined'}
                  sx={{
                    fontWeight: 600, fontSize: '0.8rem',
                    bgcolor: isActive ? V1_ACCENT : 'transparent',
                    color: isActive ? 'white' : 'text.primary',
                    borderColor: isActive ? V1_ACCENT : V1_ACCENT_BORDER,
                    '& .MuiChip-icon': { color: isActive ? 'white' : V1_ACCENT },
                    '&:hover': { bgcolor: isActive ? V1_ACCENT : V1_ACCENT_TINT },
                    transition: 'all 0.15s ease',
                  }}
                />
              );
            })}
          </Box>
        </Paper>
      </Container>

      {/* Data List — redesigned tabs (TAB_SECTIONS registry) render the RendererV1 section stack;
          the rest keep the legacy panels until their redesign lands (tab-by-tab rollout). */}
      {TAB_SECTIONS[activeCategory] ? (
        <TabRenderer
          sections={TAB_SECTIONS[activeCategory]}
          ctx={{ tab: activeCategory, openDetail: setV1Group, askPolly: onAskPolly }}
        />
      ) : (
      <Container component="main" maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2.5, pb: 12, flex: 1 }}>
        {/* My Documents renders its own header (title + upload button), so skip the duplicate h6. */}
        {activeCategory !== 'mydocuments' && (
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: '#1c1c1e' }}>
            {CATEGORIES.find(c => c.key === activeCategory)?.label}
            {activeCategory === 'trends' ? (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                {trendGroups.length} charts
              </Typography>
            ) : isStructuredTab ? (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                {(resourceData?.totalRecords ?? 0)} records · {(resourceData?.named?.length ?? 0) + (resourceData?.other ? 1 : 0)} groups
              </Typography>
            ) : null}
          </Typography>
        )}

        {activeCategory === 'mydocuments' ? (
          // The "My Documents" panel owns its own fetch/upload/SSE lifecycle.
          <UserDocumentsSection />
        ) : activeCategory === 'notes' ? (
          <NotesSection
            notes={feedNotes}
            total={feedTotal}
            onLoadMore={onNotesLoadMore}
            loadingMore={isNotesFetching}
          />
        ) : activeCategory === 'trends' ? (
          <GroupList key="trends" groups={trendGroups} loading={isTrendsLoading} emptyLabel="trends" onSelect={setDetailGroup} />
        ) : (
          <GroupList key={activeCategory} groups={structuredGroups} loading={isResourceFetching} emptyLabel={activeCategory} onSelect={setDetailGroup} />
        )}
      </Container>
      )}

      {/* Detail Dialogs — legacy tabs + the redesigned Conditions drill-down */}
      <GroupedDetailDialog group={detailGroup} resource={detailGroup?.resource || activeCategory} open={!!detailGroup} onClose={() => setDetailGroup(null)} />
      {/* Redesigned-tab drill-down dialog, per tab (v1Group is set by the ACTIVE tab's sections). */}
      {activeCategory === 'medications'
        ? <MedicationDetailDialog group={v1Group} open={!!v1Group} onClose={() => setV1Group(null)} />
        : activeCategory === 'procedures'
        ? <ProcedureDetailDialog group={v1Group} open={!!v1Group} onClose={() => setV1Group(null)} />
        : activeCategory === 'observations'
        ? <ObservationDetailDialog group={v1Group} open={!!v1Group} onClose={() => setV1Group(null)} />
        : <ConditionDetailDialog group={v1Group} open={!!v1Group} onClose={() => setV1Group(null)} />}
    </>
  );
};
